/** student/certificates — HOOKS. */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { studentCertificatesApi } from './api';
import { studentCertificatesKeys } from './keys';

export function useCampaignCertificate(campaignId: string | null) {
  return useQuery({
    queryKey: studentCertificatesKeys.status(campaignId ?? ''),
    queryFn: () => studentCertificatesApi.status(campaignId!),
    enabled: !!campaignId,
  });
}

/** Validate a typed certificate code (used by the public validation page). */
export function useValidateCertificate() {
  return useMutation({
    mutationFn: (code: string) => studentCertificatesApi.validate(code.trim().toUpperCase()),
  });
}

/**
 * Fetches the certificate PDF (when `enabled`) and exposes a blob object URL so
 * the page can render the *actual* PDF in an <iframe> — the preview is then
 * always identical to the downloaded file. Revokes the URL on cleanup.
 */
export function useCertificatePdf(campaignId: string, enabled: boolean) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState('certificate.pdf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

   
  // effect owns an async fetch, and the synchronous calls here are the reset
  // that has to happen *before* it starts: clearing stale data and raising the
  // loading flag. Moving them to render would mean computing them from a
  // previous-value comparison and still leaving the async part in an effect,
  // which is more machinery for the same behaviour. Revisit if this stops
  // fetching.
  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see the note above
      setUrl(null);
      setBlob(null);
      return;
    }
    let cancelled = false;
    let objUrl: string | null = null;
    setLoading(true);
    setError(false);
    studentCertificatesApi
      .download(campaignId)
      .then(({ blob: b, filename: fn }) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(b);
        setBlob(b);
        setUrl(objUrl);
        setFilename(fn);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [campaignId, enabled]);

  return { blob, url, filename, loading, error };
}
